import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type { HomeHeroBanner as Value } from "@reactivated";

import { getLinkUrl, originalUrl } from "../../utils/wagtail";

type IProps = Immutable<{
    value: Value;
}>;

/**
 * Faithful JSX transcription of the legacy sealy
 * ``components/hero_banners/home_hero_banner.html`` (#33285).
 *
 * Two separate regions, both gated on ``self.promotion.image``: the
 * ``hero__badge-link`` inside ``section.hero``, and the trailing
 * ``section.offer-inline`` AFTER it -- hence the fragment return. The
 * literal ``alt="…"`` on the badge img is preserved exactly as legacy
 * emitted it.
 */
export const HomeHeroBanner = (props: IProps): React.JSX.Element => {
    const value = props.value;
    const promotion = value.promotion;
    return (
        <>
            <section className="hero">
                <picture>
                    <source
                        srcSet={originalUrl(value.mobile_image)}
                        media="(max-width: 768px)"
                    />
                    <img
                        className="hero__background"
                        srcSet={originalUrl(value.image)}
                        alt={value.image?.default_alt_text ?? ""}
                    />
                </picture>

                {promotion?.image && (
                    <a
                        href={getLinkUrl(promotion.link)}
                        className="hero__badge-link"
                        title={promotion.link?.html_title ?? ""}
                    >
                        <img
                            className="hero__badge img-responsive"
                            srcSet={originalUrl(promotion.image)}
                            alt="…"
                        />
                    </a>
                )}

                <div className="hero__content hero__content--home container">
                    {value.logo && (
                        <svg className="hero__logo logo-pptech">
                            <use xlinkHref="#logo-pptech" />
                        </svg>
                    )}
                    <h1 className="hero__headline">{value.title}</h1>
                    <a
                        className="hero__cta h4"
                        href={getLinkUrl(value.link)}
                        title={value.link?.html_title ?? ""}
                    >
                        {value.link?.display_name}
                    </a>
                </div>

                <div className="value-props container">
                    <div className="value-props__item">
                        <figure className="value-props__icon">
                            <svg className="icon-support">
                                <title>{t`Mattress Icon`}</title>
                                <use xlinkHref="#icon-support" />
                            </svg>
                        </figure>
                        <div className="value-props__copy">
                            <h4 className="value-props__headline">
                                {t`Posturepedic Technology`}
                            </h4>
                            <p className="value-props__subtext">
                                {t`Exclusive to Sealy`}
                            </p>
                        </div>
                    </div>

                    <div className="value-props__item">
                        <figure className="value-props__icon">
                            <svg className="icon-support">
                                <title>{t`Sealy Certified Icon`}</title>
                                <use xlinkHref="#icon-certified" />
                            </svg>
                        </figure>
                        <div className="value-props__copy">
                            <h4 className="value-props__headline">
                                {t`Exceptional Warranty`}
                            </h4>
                            <p className="value-props__subtext">
                                {t`Up to 10 years`}
                            </p>
                        </div>
                    </div>

                    <div className="value-props__item">
                        <figure className="value-props__icon">
                            <svg className="icon-support">
                                <title>{t`Heart Icon`}</title>
                                <use xlinkHref="#icon-love" />
                            </svg>
                        </figure>
                        <div className="value-props__copy">
                            <h4 className="value-props__headline">
                                {t`Over 100 Million Sold`}
                            </h4>
                            <p className="value-props__subtext">
                                {t`A history of 130 years`}
                            </p>
                        </div>
                    </div>

                    <div className="value-props__item">
                        <figure className="value-props__icon">
                            <svg className="icon-support">
                                <title>{t`USA Flag Icon`}</title>
                                <use xlinkHref="#icon-usa" />
                            </svg>
                        </figure>
                        <div className="value-props__copy">
                            <h4 className="value-props__headline">
                                {t`Made in the USA`}
                            </h4>
                            <p className="value-props__subtext">
                                {t`American made & manufactured`}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {promotion?.image && (
                <section className="offer-inline">
                    <h3 className="offer-inline__lead-in">{promotion.title}</h3>
                    <h2 className="offer-inline__headline">
                        {promotion.subtitle}
                    </h2>
                    <h5 className="offer-inline__date">{promotion.dates}</h5>
                    <a
                        href={getLinkUrl(promotion.link)}
                        className="offer-inline__cta"
                        title={promotion.link?.html_title ?? ""}
                    >
                        {promotion.link?.display_name}
                    </a>
                </section>
            )}
        </>
    );
};
